import type { DateTime } from 'luxon'
import { z } from 'zod'

import type {
  COURSE_SLUG_SAT_BOOTCAMP_MATH,
  COURSE_SLUG_SAT_BOOTCAMP_READING,
  ResearchGroup,
  SATUserType,
} from '@/queries/constants'

import type { FormInputType } from '@/components/UI/Form/types'

export type SATBootcampCertificationState = 'auto' | 'certified' | 'certification-pending' | 'none'

export type SATBootcampInterventionTaskDetails = {
  id: number
  name: string
  link: string
  releaseDate: DateTime
  dueDate: DateTime | null
  satTestId: number
  satTestInterventionArmId: number
  reminderHistory: DateTime[]
  forLearners: boolean
}

export type SATBootcampInterventionArmDetails = {
  id: number
  name: string
  group: string
  userType: SATUserType
  weight: number
  satTestTitle: string
  countUsers: number
  createdAt: DateTime
  tasks: SATBootcampInterventionTaskDetails[]
}

export type SATBootcampLearnerOnboardingState = {
  parentEmail: string
  parentConsentRequestedAt: DateTime | null
  parentConsentCompletedAt: DateTime | null
  parentConsentWaivedAt: DateTime | null
  assentCompletedAt: DateTime | null
  crosswalkId: string | null
  registeredForBootcamp: boolean
}

export type SATBootcampTestDetails = {
  title: string
  id: number
  testDate: DateTime
  tutorReleasedAt: DateTime
  seriesCreationDeadline: DateTime
  learnerReleasedAt: DateTime
  learnerRegistrationDeadline: DateTime
  onboardingSessionReleasedAt: DateTime
  onboardingSessionDeadline: DateTime
  startDate: DateTime
  endDate: DateTime
}

export type SATBootcampCourseSlug =
  | typeof COURSE_SLUG_SAT_BOOTCAMP_MATH
  | typeof COURSE_SLUG_SAT_BOOTCAMP_READING

export type Assent = {
  type: SATUserType
  satUserId: number
  collegeBoardId: string | null
  testSchedule: string
  assentData: Record<string, unknown>
}

export const TUTORING_ELIGIBILITY = ['both', 'ebrw', 'math'] as const
export type TutoringEligibility = (typeof TUTORING_ELIGIBILITY)[number]

export const SAT_RESEARCH_GROUP_SLUG = [
  'may-23-reading',
  'may-23-math',
  'june-23-reading',
  'june-23-math',
  'august-24',
  'december-24-zinc',
  'december-24-text-structure',
  'december-24-control',
] as const
export type SATResearchGroupSlug = (typeof SAT_RESEARCH_GROUP_SLUG)[number]

export const SAT_RESEARCH_GROUP_SLUG_TO_RESEARCH_GROUP: Record<
  SATResearchGroupSlug,
  ResearchGroup
> = {
  'may-23-math': 'may-2023-sat-bootcamp-rct-math',
  'may-23-reading': 'may-2023-sat-bootcamp-rct-reading',
  'june-23-math': 'june-2023-sat-bootcamp-rct-math',
  'june-23-reading': 'june-2023-sat-bootcamp-rct-reading',
  'august-24': 'august-2024-sat-bootcamp-rct',
  'december-24-zinc': 'december-2024-sat-bootcamp-rct-zinc',
  'december-24-text-structure': 'december-2024-sat-bootcamp-rct-text-structure',
  'december-24-control': 'december-2024-sat-bootcamp-rct-control',
}

// Don't use transforms in this schema. Reason: we use it on the frontend to parse the invitation params and on the backend as input. If we transform it on the frontend, it will be invalid when passed to the backend.
export const satInvitationParamsSchema = z.object({
  cwid: z.string(),
  name: z.string(),
  tutor: z.enum(TUTORING_ELIGIBILITY).optional(),
  research: z.enum(SAT_RESEARCH_GROUP_SLUG).optional(),
  belowThreshold: z.enum(TUTORING_ELIGIBILITY).optional(),
  psat: z.union([z.literal('true'), z.literal('false')]).optional(),
})

export type SATInvitationParams = z.infer<typeof satInvitationParamsSchema>

export type SATBootcampURLParams =
  | {
      type: 'invite-code'
      inviteCode: string
    }
  | ({
      type: 'encoded-params'
    } & SATInvitationParams)

export type SATBootcampSeriesDetails = {
  id: number
  sessionStartDates: string[]
  tutorName: string
  tutorAvatarUrl: string
  tutorBio: string
  templateSlug: string
  satScoreBandName: string
  countRegistrationsAndReservations: number
  maxLearners: number
  attendancePolicy: string
  questionnaire: FormInputType[] | null
  researchGroup: ResearchGroup | null
  satTestId: number
}

interface CollegeBoardQuestionConfigBase {
  type: CollegeBoardQuestionType
  options: string[] | null
  answers: CollegeBoardQuestionAnswers
}

export const COLLEGE_BOARD_MULTIPLE_CHOICE_OPTIONS = ['a', 'b', 'c', 'd'] as const
export type CollegeBoardMultipleChoiceOption =
  (typeof COLLEGE_BOARD_MULTIPLE_CHOICE_OPTIONS)[number]

interface CollegeBoardQuestionConfigMCQ extends CollegeBoardQuestionConfigBase {
  type: 'multiple-choice'
  answers: CollegeBoardMultipleChoiceOption[]
  options: string[]
}

interface CollegeBoardQuestionConfigSPR extends CollegeBoardQuestionConfigBase {
  type: 'student-produced-response'
  answers: string[]
  options: null
}

export type CollegeBoardQuestionConfig =
  | CollegeBoardQuestionConfigMCQ
  | CollegeBoardQuestionConfigSPR

export type CollegeBoardQuestionType = CollegeBoardQuestionConfig['type']
export type CollegeBoardQuestionAnswers = CollegeBoardQuestionConfig['answers']
export type CollegeBoardQuestionOptions = CollegeBoardQuestionConfig['options']

export type SATAssessmentsVersion = 'original' | 'dsat-1' | 'dsat-2'
